import React, { useCallback } from "react";
import { trackComponent } from "@repo/analytics";
import {
  componentNameConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  productCategory,
  referenceId,
  sprint,
  subSection,
} from "../../../pages/News/newsTrackingConstants";

interface SwiperNavigationButtonsProps {
  showLeftButton: boolean;
  showRightButton: boolean;
  onScrollPrev: () => void;
  onScrollNext: () => void;
  isMobile?: boolean;
  isOpenInApp?: boolean;
  headline: string;
  componentPosition?: string;
}

/**
 * Mobile right arrow SVG component
 */
const MobileRightArrow: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="36" viewBox="0 0 30 36" fill="none">
    <mask id="path-1-inside-1_110_30821" fill="white">
      <path d="M0 8C0 3.58172 3.58172 0 8 0H30V36H8C3.58172 36 0 32.4183 0 28V8Z"/>
    </mask>
    <path d="M0 8C0 3.58172 3.58172 0 8 0H30V36H8C3.58172 36 0 32.4183 0 28V8Z" fill="white"/>
    <path d="M-1 8C-1 3.02944 3.02944 -1 8 -1H30V1H8C4.13401 1 1 4.13401 1 8H-1ZM30 37H8C3.02944 37 -1 32.9706 -1 28H1C1 31.866 4.13401 35 8 35H30V37ZM8 37C3.02944 37 -1 32.9706 -1 28V8C-1 3.02944 3.02944 -1 8 -1V1C4.13401 1 1 4.13401 1 8V28C1 31.866 4.13401 35 8 35V37ZM30 0V36V0Z" fill="#CCCCCC" mask="url(#path-1-inside-1_110_30821)"/>
    <path d="M16.5022 23.6948C16.1905 24.0662 15.649 24.1039 15.2927 23.7789C14.9639 23.4789 14.9078 22.9748 15.1463 22.6069L15.2121 22.5179L19.0046 18L15.2121 13.4821C14.9243 13.1392 14.933 12.6318 15.2162 12.3002L15.2927 12.2211C15.6216 11.9211 16.1083 11.9301 16.4264 12.2254L16.5022 12.3052L20.7879 17.4115C21.0471 17.7204 21.0687 18.1688 20.8527 18.501L20.7879 18.5884L16.5022 23.6948Z" fill="#1A1A1A"/>
  </svg>
);

/**
 * Mobile left arrow SVG component (inverted right arrow)
 */
const MobileLeftArrow: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="36" viewBox="0 0 30 36" fill="none" style={{ transform: 'scaleX(-1)' }}>
    <mask id="path-1-inside-1_110_30821-left" fill="white">
      <path d="M0 8C0 3.58172 3.58172 0 8 0H30V36H8C3.58172 36 0 32.4183 0 28V8Z"/>
    </mask>
    <path d="M0 8C0 3.58172 3.58172 0 8 0H30V36H8C3.58172 36 0 32.4183 0 28V8Z" fill="white"/>
    <path d="M-1 8C-1 3.02944 3.02944 -1 8 -1H30V1H8C4.13401 1 1 4.13401 1 8H-1ZM30 37H8C3.02944 37 -1 32.9706 -1 28H1C1 31.866 4.13401 35 8 35H30V37ZM8 37C3.02944 37 -1 32.9706 -1 28V8C-1 3.02944 3.02944 -1 8 -1V1C4.13401 1 1 4.13401 1 8V28C1 31.866 4.13401 35 8 35V37ZM30 0V36V0Z" fill="#CCCCCC" mask="url(#path-1-inside-1_110_30821-left)"/>
    <path d="M16.5022 23.6948C16.1905 24.0662 15.649 24.1039 15.2927 23.7789C14.9639 23.4789 14.9078 22.9748 15.1463 22.6069L15.2121 22.5179L19.0046 18L15.2121 13.4821C14.9243 13.1392 14.933 12.6318 15.2162 12.3002L15.2927 12.2211C15.6216 11.9211 16.1083 11.9301 16.4264 12.2254L16.5022 12.3052L20.7879 17.4115C21.0471 17.7204 21.0687 18.1688 20.8527 18.501L20.7879 18.5884L16.5022 23.6948Z" fill="#1A1A1A"/>
  </svg>
);

export const SwiperNavigationButtons: React.FC<SwiperNavigationButtonsProps> = ({
  showLeftButton,
  showRightButton,
  onScrollPrev,
  onScrollNext,
  isMobile = false,
  isOpenInApp = false,
  headline,
  componentPosition = "1",
}) => {
  const desktopButtonClass =
    "absolute top-1/2 -translate-y-1/2 w-[42px] h-[48px] bg-white/95 border border-[#E4E7EC] shadow-[0_10px_30px_rgba(0,0,0,0.08)] cursor-pointer flex items-center justify-center transition-all duration-150 hover:shadow-lg hover:scale-[1.02] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff6b35] ";
  
  const mobileButtonClass =
    "absolute cursor-pointer flex items-center justify-center transition-all duration-150 hover:scale-[1.02] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff6b35] ";

  // For mobile, position arrows at different heights for mobile app vs mobile web
  // Mobile App: 252px from bottom
  // Mobile Web: 279px from bottom
  const mobileVerticalPositionClass = isOpenInApp ? "bottom-[80px]" : "bottom-[160px]";

  const trackLearningCenterClick = useCallback(
    async (direction: "prev" | "next") => {
      try {
        const customerId = await getCustomerIdForAnalytics();

        await trackComponent({
          componentName: componentNameConstants.LEARNING_CENTER,
          sectionTitle: headline,
          eventType: dlEventConstants.LEARNING_CENTER_ICON_CLICK,
          ctaText: direction === "prev" ? "previous" : "next",
          productCategory,
          subSection,
          cleverTapOverrides: {
            EP_JOURNEY: journeyTag,
            EP_PROPERTY_TYPE: "pdp",
            EP_PRODUCT_NAME: subSection,
            EP_CAMPAIGN: campaignName,
            EP_SPRINT: sprint,
            EP_REFERENCE_ID: referenceId,
            ...(customerId && { EP_CUSTOMER_ID: customerId }),
            EP_SR_NUMBER: componentPosition,
            EP_CUST_ATTRIBUTE_1: "full news",
          },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to track learning_center click", error);
      }
    },
    [componentPosition, headline],
  );

  if (isMobile) {
    return (
      <>
        {showLeftButton && (
          <button
            onClick={() => {
              trackLearningCenterClick("prev");
              onScrollPrev();
            }}
            className={`${mobileButtonClass} ${mobileVerticalPositionClass} left-0`}
            aria-label="Scroll left"
          >
            <MobileLeftArrow />
          </button>
        )}
        {showRightButton && (
          <button
            onClick={() => {
              trackLearningCenterClick("next");
              onScrollNext();
            }}
            className={`${mobileButtonClass} ${mobileVerticalPositionClass} right-0`}
            aria-label="Scroll right"
          >
            <MobileRightArrow />
          </button>
        )}
      </>
    );
  }

  return (
    <>
      {showLeftButton && (
        <button
          onClick={() => {
            trackLearningCenterClick("prev");
            onScrollPrev();
          }}
          className={`${desktopButtonClass} left-0 pt-2 pr-1.5 pb-2 pl-1 rounded-tr-[8px] rounded-br-[8px]`}
          aria-label="Scroll left"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-6 h-6 text-[#ff6b35]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      {showRightButton && (
        <button
          onClick={() => {
            trackLearningCenterClick("next");
            onScrollNext();
          }}
          className={`${desktopButtonClass} right-0 pt-2 pr-1 pb-2 pl-1.5 rounded-tl-[8px] rounded-bl-[8px]`}
          aria-label="Scroll right"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-6 h-6 text-[#ff6b35]"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      )}
    </>
  );
};

